import { useEffect, useRef, useState } from 'preact/hooks';
import { createChart, CandlestickSeries, HistogramSeries, type IChartApi, type ISeriesApi, type UTCTimestamp } from 'lightweight-charts';
import { api, onEntry } from '../api.js';
import { useStore } from '../store.js';
import type { SignedRequest } from '../../../src/core/types.js';

const INTERVALS: [string, number][] = [
  ['15s', 15],
  ['1m', 60],
  ['5m', 300],
  ['1h', 3600],
];

/** Price chart in ZEC per token, fed from the trade log. */
export function Chart({ tokenId }: { tokenId: string }) {
  const box = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const candles = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volume = useRef<ISeriesApi<'Histogram'> | null>(null);
  const [interval, setInterval_] = useState(60);
  const info = useStore().info;
  // Prices of a few billionths of a ZEC are unreadable, so the chart shows market cap (price × supply) like other launchpads.
  const supply = info ? Number(BigInt(info.genesis.totalSupply) / 10n ** BigInt(info.genesis.tokenDecimals)) : 1e9;

  useEffect(() => {
    if (!box.current) return;
    const css = getComputedStyle(document.documentElement);
    const v = (n: string) => css.getPropertyValue(n).trim();
    const c = createChart(box.current, {
      autoSize: true,
      layout: { background: { color: v('--vault-2') }, textColor: v('--mute'), fontFamily: 'Figtree, sans-serif', attributionLogo: false },
      grid: { vertLines: { color: 'transparent' }, horzLines: { color: v('--line') } },
      rightPriceScale: { borderColor: v('--line') },
      timeScale: { borderColor: v('--line'), timeVisible: true, secondsVisible: false },
      crosshair: { mode: 0 },
      localization: { locale: safeLocale() },
    });
    candles.current = c.addSeries(CandlestickSeries, {
      upColor: v('--buy'),
      downColor: v('--sell'),
      borderVisible: false,
      wickUpColor: v('--buy'),
      wickDownColor: v('--sell'),
      priceFormat: { type: 'custom', minMove: 1e-12, formatter: (p: number) => fmtCap((p / 1e8) * supply) },
    });
    volume.current = c.addSeries(HistogramSeries, { priceScaleId: '', color: v('--line'), priceFormat: { type: 'volume' } });
    volume.current.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    chart.current = c;
    return () => {
      c.remove();
      chart.current = null;
    };
  }, [supply]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const data = await api.candles(tokenId, interval);
      if (!alive || !candles.current || !volume.current) return;
      candles.current.setData(data.map((k) => ({ time: (k.t / 1000) as UTCTimestamp, open: k.o, high: k.h, low: k.l, close: k.c })));
      volume.current.setData(data.map((k) => ({ time: (k.t / 1000) as UTCTimestamp, value: k.v / 1e8 })));
    };
    void load();
    const off = onEntry((e) => {
      const b = (e.data as SignedRequest)?.req?.body;
      if (b && (b.type === 'buy' || b.type === 'sell') && b.token === tokenId) void load();
    });
    return () => {
      alive = false;
      off();
    };
  }, [tokenId, interval]);

  return (
    <div>
      <div class="chart-box" ref={box} />
      <div class="chart-tools" role="group" aria-label="Candle size">
        {INTERVALS.map(([label, sec]) => (
          <button class="chip" aria-pressed={interval === sec} onClick={() => setInterval_(sec)} key={label}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function safeLocale(): string {
  try {
    const l = navigator.language;
    new Intl.NumberFormat(l);
    return l;
  } catch {
    return 'en-US';
  }
}

/** Market cap in ZEC, compact: 0.84, 12.3, 1.2k */
function fmtCap(z: number): string {
  if (z >= 1000) return (z / 1000).toFixed(z >= 10_000 ? 0 : 1) + 'k ZEC';
  if (z >= 100) return z.toFixed(0) + ' ZEC';
  if (z >= 10) return z.toFixed(1) + ' ZEC';
  return z.toFixed(2) + ' ZEC';
}
